import { useCallback, useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Edit, Trash2, ShieldCheck, Loader2 } from "lucide-react";
import { getAdminRoles, createAdminRole, updateAdminRole, deleteAdminRole, getPermissionAreas } from "@/lib/api";
import { PermissionGrid } from "@/components/shared/PermissionGrid";
import { useAdminAccess } from "@/lib/adminAccess";

export default function AdminRoles() {
  const [roles, setRoles] = useState([]);
  const [areas, setAreas] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const { can } = useAdminAccess();
  const canManage = can("user_roles", "manage");
  const load = useCallback(() => getAdminRoles().then(setRoles).catch(() => toast.error("Failed to load roles")), []);
  useEffect(() => { load(); getPermissionAreas().then(setAreas).catch(() => {}); }, [load]);

  const save = async () => {
    if (!form.name?.trim()) return toast.error("Role name is required");
    setSaving(true);
    try {
      if (form.id) await updateAdminRole(form.id, form); else await createAdminRole(form);
      toast.success(form.id ? "Role updated. Everyone on this role has the new permissions." : "Role created");
      setForm(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save role"); }
    setSaving(false);
  };
  const remove = async (r) => {
    if (!window.confirm(`Delete role "${r.name}"?`)) return;
    try { await deleteAdminRole(r.id); toast.success("Role deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to delete role"); }
  };
  const count = (p) => Object.values(p || {}).filter((v) => v !== "none").length;

  return (
    <div className="space-y-4" data-testid="admin-roles-page">
      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-500">Security roles group permissions. Editing a role updates every user on it.</p>
        {canManage && <Button onClick={() => setForm({ name: "", description: "", permissions: {} })} className="bg-teal hover:bg-teal-dark text-white" data-testid="create-role-btn"><Plus className="w-4 h-4 mr-2" />New Role</Button>}
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {roles.map((r) => (
          <Card key={r.id} className="border-slate-200" data-testid={`role-card-${r.id}`}>
            <CardContent className="p-5 space-y-2">
              <div className="flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-teal" /><p className="font-heading font-semibold text-navy">{r.name}</p></div>
              {r.description && <p className="text-xs text-slate-500">{r.description}</p>}
              <p className="text-xs text-slate-500">{count(r.permissions)} areas · {r.user_count} user(s)</p>
              <div className="flex gap-2 pt-2 border-t border-slate-100">
                <Button variant="outline" size="sm" className="flex-1" onClick={() => setForm({ ...r })} data-testid={`edit-role-${r.id}`}><Edit className="w-4 h-4 mr-1" />{canManage ? "Edit" : "View"}</Button>
                {canManage && <Button variant="outline" size="sm" className="text-red-600" onClick={() => remove(r)} data-testid={`delete-role-${r.id}`}><Trash2 className="w-4 h-4" /></Button>}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{form?.id ? (canManage ? "Edit Role" : "Role") : "New Role"}</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label>Role name *</Label><Input value={form.name} disabled={!canManage} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="role-name" /></div>
                <div><Label>Description</Label><Input value={form.description || ""} disabled={!canManage} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="role-description" /></div>
              </div>
              <PermissionGrid areas={areas} value={form.permissions} disabled={!canManage} onChange={(p) => setForm({ ...form, permissions: p })} />
              {canManage && <Button onClick={save} disabled={saving} className="w-full bg-teal hover:bg-teal-dark text-white" data-testid="save-role-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Role"}</Button>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
